import { makeRequest, apiClient } from '../../../../services/apiClient';

// Detect 404 or "Not Found" errors robustly
const isNotFound = (err) => {
  if (!err) return false;
  const status = err?.response?.status;
  if (status === 404) return true;
  const msg = String(err?.message || err || '');
  return /Not Found/i.test(msg) || /status code\s*404/i.test(msg) || /\b404\b/.test(msg);
};

// Derive backend origin from axios baseURL or window origin
const getBackendOrigin = () => {
  try {
    const base = apiClient?.defaults?.baseURL || '';
    if (!base) return null;
    const u = new URL(base, window?.location?.origin);
    return u.origin; // e.g., http://localhost:8000
  } catch (e) {
    return null;
  }
};

/**
 * Fetch KPI for events with missing/invalid activity labels.
 * Tries multiple endpoints including primary, alias, and simple forms, with
 * robust fallbacks for different mount points (with/without /api, /v1, gateway).
 * @param {string|null} fileType Optional OCEL file type to analyze
 * @returns {Promise<{file_type:string,total_events:number,invalid_activity_events:number,invalid_ratio:number,considered_keys?:string[]}>}
 */
export const fetchMissingActivityKPI = async (fileType = null) => {
  const params = fileType ? { file_type: fileType } : {};

  // 0) Try primary endpoints quickly - KPI router is mounted at /api/kpi
  try {
    return await makeRequest('/kpi/missing-activities', { params }, 'missing activities KPI');
  } catch (e) {
    if (!isNotFound(e)) throw e;
  }
  try {
    return await makeRequest('/missing-activities', { params }, 'missing activities KPI (legacy)');
  } catch (e) {
    if (!isNotFound(e)) throw e;
  }

  // 1) Try simple endpoints (local mount first)
  let simple;
  try {
    simple = await makeRequest('/missing-activities-simple', { params }, 'missing activities KPI (simple)');
  } catch (e3) {
    if (!isNotFound(e3)) throw e3;
    try {
      simple = await makeRequest('/analytics/missing-activities-simple', { params }, 'missing activities KPI (simple alias)');
    } catch (e4) {
      if (!isNotFound(e4)) throw e4;

      // 1c) Try broader set of candidate URLs to handle different deployments
      if (typeof window !== 'undefined' && window.location) {
        const origin = getBackendOrigin() || window.location.origin;
        const candidates = [
          // Primary
          `${origin}/missing-activities`,
          `${origin}/api/missing-activities`,
          `${origin}/api/v1/missing-activities`,
          `${origin}/v1/missing-activities`,
          `${origin}/analytics/missing-activities`,
          `${origin}/api/analytics/missing-activities`,
          `${origin}/api/v1/analytics/missing-activities`,
          `${origin}/v1/analytics/missing-activities`,
          // Simple
          `${origin}/missing-activities-simple`,
          `${origin}/api/missing-activities-simple`,
          `${origin}/api/v1/missing-activities-simple`,
          `${origin}/v1/missing-activities-simple`,
          `${origin}/analytics/missing-activities-simple`,
          `${origin}/api/analytics/missing-activities-simple`,
          `${origin}/api/v1/analytics/missing-activities-simple`,
          `${origin}/v1/analytics/missing-activities-simple`,
          // Gateway variants
          `${origin}/gateway/api/missing-activities`,
          `${origin}/gateway/api/analytics/missing-activities`,
          `${origin}/gateway/api/missing-activities-simple`,
          `${origin}/gateway/api/analytics/missing-activities-simple`,
        ];
        for (const url of candidates) {
          try {
            // Try primary first among candidates
            if (/missing-activities(?!-simple)/.test(url)) {
              const res = await makeRequest(url, { params }, `missing activities KPI (${url})`);
              if (res && typeof res === 'object' && 'total_events' in res) return res;
            }
          } catch (e5) {
            if (!isNotFound(e5)) throw e5;
          }
        }
        // If primary variants failed, try simple variants among candidates
        for (const url of candidates) {
          if (/missing-activities-simple/.test(url)) {
            try {
              simple = await makeRequest(url, { params }, `missing activities KPI (simple ${url})`);
              if (simple) break;
            } catch (e6) {
              if (!isNotFound(e6)) throw e6;
            }
          }
        }
      }
    }
  }

  const invalidCount = typeof simple?.missing_activities === 'number' ? simple.missing_activities : 0;

  // 2) Fetch total events to compute ratio (with robust fallbacks)
  let total = 0;
  try {
    const totalResp = await makeRequest('/total-events', { params }, 'total events for missing activities');
    total = typeof totalResp?.totalEvents === 'number' ? totalResp.totalEvents : 0;
  } catch (te) {
    if (!isNotFound(te)) throw te;
    try {
      const totalResp2 = await makeRequest('/ocel/total-events', { params }, 'total events for missing activities (alias)');
      total = typeof totalResp2?.totalEvents === 'number' ? totalResp2.totalEvents : 0;
    } catch (te2) {
      if (!isNotFound(te2)) throw te2;
      if (typeof window !== 'undefined' && window.location) {
        const origin = getBackendOrigin() || window.location.origin;
        const totalCandidates = [
          `${origin}/total-events`,
          `${origin}/api/total-events`,
          `${origin}/api/v1/total-events`,
          `${origin}/v1/total-events`,
          `${origin}/ocel/total-events`,
          `${origin}/api/ocel/total-events`,
          `${origin}/api/v1/ocel/total-events`,
          `${origin}/v1/ocel/total-events`,
          `${origin}/gateway/api/total-events`,
          `${origin}/gateway/api/ocel/total-events`,
        ];
        for (const url of totalCandidates) {
          try {
            const totalRespX = await makeRequest(url, { params }, `total events for missing activities (${url})`);
            total = typeof totalRespX?.totalEvents === 'number' ? totalRespX.totalEvents : 0;
            if (total > 0) break;
          } catch (e7) {
            if (!isNotFound(e7)) throw e7;
          }
        }
      }
    }
  }

  const ratio = total > 0 ? invalidCount / total : 0;
  return {
    file_type: fileType ?? 'main',
    total_events: total,
    invalid_activity_events: invalidCount,
    invalid_ratio: ratio,
    considered_keys: ['ocel:activity']
  };
};
